import { Badge, Modal } from '@sandtech-hub-monorepo/ui-kit';
import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { EnglishProficiencyModal } from './EnglishProficiencyModal';
import {
  EnglishProficiencyStatus,
  GetViewerProgramEnrollmentsWithApplicationDocument,
  HighSchoolCertificateStatus,
  ManagePersonaInquiryDocument,
  PersonaInquiryType,
  ProgramApplicationStatus,
} from '@sandtech-hub-monorepo/ehub-client';
import { Application, Enrollment } from '../types';
import { IDVerificationModal } from './modals/IDVerificationModal';
import { EducationVerificationModal } from './modals/EducationVerificationModal';
import { PriorLearningModal } from './modals/PriorLearningModal';
import { EnrollmentAgreementModal } from './modals/EnrollmentAgreementModal';
import { VerificationStep } from './VerificationStep';
import {
  getDocusignStatus,
  getPaymentPlanStatus,
  getPersonaInquiryStatus,
  isDocusignSigned,
  VerificationStatus,
} from './verificationUtils';
import { AppRoutesPaths } from '../../../routes/types';

interface Props {
  enrollment: Enrollment;
  application: Application;
}

export const ApplicationRequirements = ({ enrollment, application }: Props) => {
  const verificationState = {
    identityVerification: getPersonaInquiryStatus(
      application.personaIdentityInquiry
    ),
    secondaryEducation: VerificationStatus.NotSubmitted,
    enrollmentAgreement: getDocusignStatus(
      application.docusignEnrollmentAgreement
    ),
  };

  const navigate = useNavigate();
  const [englishModalOpen, setEnglishModalOpen] = useState(false);
  const [idModalOpen, setIdModalOpen] = useState(false);
  const [educationModalOpen, setEducationModalOpen] = useState(false);
  const [priorLearningModalOpen, setPriorLearningModalOpen] = useState(false);
  const [enrollmentAgreementModalOpen, setEnrollmentAgreementModalOpen] =
    useState(false);

  const [managePersonaInquiry] = useMutation(ManagePersonaInquiryDocument, {
    onCompleted: (data) => {
      if (data.managePersonaInquiry.redirectUrl) {
        window.open(data.managePersonaInquiry.redirectUrl, '_blank');
      }
    },
    onError: (error) => {
      console.error('Failed to manage Persona inquiry:', error);
    },
    refetchQueries: [GetViewerProgramEnrollmentsWithApplicationDocument],
  });

  // Determine high school certificate status
  const getHighSchoolCertificateStatus = () => {
    // Check if using Persona verification
    if (application.highSchoolVerificationMode === 'PERSONA') {
      return getPersonaInquiryStatus(application.personaHighschoolInquiry);
    }

    // Check manual verification (high school certificate)
    if (application.highSchoolCertificate) {
      const { status } = application.highSchoolCertificate;

      // Map HighSchoolCertificateStatus to VerificationStatus
      switch (status) {
        case HighSchoolCertificateStatus.Passed:
          return VerificationStatus.Completed;
        case HighSchoolCertificateStatus.Pending:
          return VerificationStatus.Pending;
        case HighSchoolCertificateStatus.Failed:
          return VerificationStatus.Failed;
        case HighSchoolCertificateStatus.NotSubmitted:
        case HighSchoolCertificateStatus.RequiresResubmission:
          return VerificationStatus.NotSubmitted;
        default:
          return VerificationStatus.NotSubmitted;
      }
    }

    return VerificationStatus.NotSubmitted;
  };

  const highSchoolCertificateStatus = getHighSchoolCertificateStatus();

  const isAgreementSigned = isDocusignSigned(
    application.docusignEnrollmentAgreement
  );

  return (
    <>
      {/* Requirements Steps */}
      <div className="flex-1 mt-6">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-heading">
            Application requirements
          </h2>
          {application.status === ProgramApplicationStatus.Pending && (
            <Badge variant="warning">Pending</Badge>
          )}
          {application.status === ProgramApplicationStatus.Rejected && (
            <Badge variant="destructive">Rejected</Badge>
          )}
          {application.status === ProgramApplicationStatus.Success && (
            <Badge variant="success">Approved</Badge>
          )}
        </div>

        <div className="relative">
          {/* Roadmap connecting line - positioned to connect between all dots including final */}
          <div
            className="absolute left-3 w-0.5 bg-border"
            style={{
              top: '1.5rem', // Start at first dot center
              bottom: '1.5rem', // End at final dot center
            }}
          ></div>

          <div className="space-y-0">
            {/* Identity Verification */}
            {verificationState && (
              <VerificationStep
                title="Identification Verification"
                description="Please submit your government ID document for verification"
                pendingDescription="Your ID document is currently being verified"
                failedDescription="We couldn't verify your ID document. Please upload the correct document"
                completedDescription="We have successfully processed your identity verification"
                resumeDescription="Resume your identity verification to continue"
                status={verificationState.identityVerification}
                onModalOpen={() => setIdModalOpen(true)}
                managePersonaInquiryMutation={(action) =>
                  managePersonaInquiry({
                    variables: {
                      input: {
                        programApplicationId: application.id,
                        inquiryType: PersonaInquiryType.Identity,
                        action: action,
                      },
                    },
                  })
                }
              />
            )}

            {/* High School Certificate */}
            <VerificationStep
              verificationMode={application.highSchoolVerificationMode}
              programApplicationId={application.id}
              title="High School Certificate"
              description="Upload your secondary education certificate"
              pendingDescription="Your high school certificate is currently being verified"
              failedDescription="We couldn't verify your high school certificate. Please upload the correct document"
              completedDescription="We have successfully processed your high school verification"
              resumeDescription="Resume your high school certificate verification to continue"
              status={highSchoolCertificateStatus}
              onModalOpen={() => setEducationModalOpen(true)}
              managePersonaInquiryMutation={(action) =>
                managePersonaInquiry({
                  variables: {
                    input: {
                      programApplicationId: application.id,
                      inquiryType: PersonaInquiryType.Highschool,
                      action: action,
                    },
                  },
                })
              }
            />

            {/* English Proficiency */}
            <VerificationStep
              title="English Proficiency"
              description="Please confirm your English proficiency"
              pendingDescription="Your English proficiency is currently being verified"
              failedDescription="We couldn't verify your English proficiency. Please upload the correct document"
              completedDescription="Your English proficiency has been confirmed"
              status={(() => {
                switch (application.englishProficiencyStatus) {
                  case EnglishProficiencyStatus.Passed:
                    return VerificationStatus.Completed;
                  case EnglishProficiencyStatus.Pending:
                    return VerificationStatus.Pending;
                  case EnglishProficiencyStatus.Failed:
                    return VerificationStatus.Failed;
                  case EnglishProficiencyStatus.NotSubmitted:
                  case EnglishProficiencyStatus.RequiresResubmission:
                    return VerificationStatus.NotSubmitted;
                  default:
                    return VerificationStatus.NotSubmitted;
                }
              })()}
              onModalOpen={() => setEnglishModalOpen(true)}
            />

            {/* Enrollment Agreement */}
            {verificationState && (
              <VerificationStep
                title="Enrollment Agreement"
                description="Sign your enrollment agreement to complete the process"
                completedDescription="Your enrollment agreement has been signed"
                pendingDescription="Complete all the required steps from above"
                status={verificationState.enrollmentAgreement}
                onModalOpen={() => {
                  if (isAgreementSigned) {
                    const link = application.docusignEnrollmentAgreement?.link;
                    if (link) {
                      window.open(link, '_blank');
                    }
                  } else {
                    setEnrollmentAgreementModalOpen(true);
                  }
                }}
                actionText={
                  isAgreementSigned ? 'View Signed Agreement' : 'Sign Agreement'
                }
                disabledReason="Complete all mandatory requirements to unlock this step"
                showButtonWhenCompleted={true}
              />
            )}

            {/* Payment Plan */}
            {enrollment.paymentPlan && (
              <VerificationStep
                title="Payment plan"
                description="Make your first payment towards your program"
                completedDescription="Your payment has been successfully processed"
                status={getPaymentPlanStatus(
                  enrollment.paymentPlan,
                  isAgreementSigned
                )}
                onModalOpen={() => {
                  const viewUrl = enrollment.paymentPlan?.viewUrl;
                  if (viewUrl) {
                    window.open(viewUrl, '_blank');
                  } else {
                    navigate(AppRoutesPaths.Settings);
                  }
                }}
                actionText="Make payment"
                isDisabled={!isAgreementSigned}
                disabledReason="Sign your enrollment agreement to unlock this step"
              />
            )}
          </div>
        </div>
      </div>
      {/* Modals */}
      <Modal open={englishModalOpen} onOpenChange={setEnglishModalOpen}>
        <EnglishProficiencyModal
          close={() => setEnglishModalOpen(false)}
          applicationId={application.id}
        />
      </Modal>

      {verificationState && (
        <>
          <Modal open={idModalOpen} onOpenChange={setIdModalOpen}>
            <IDVerificationModal
              close={() => setIdModalOpen(false)}
              application={application}
            />
          </Modal>

          <Modal open={educationModalOpen} onOpenChange={setEducationModalOpen}>
            <EducationVerificationModal
              referenceId={application.user.sandId!}
              close={() => setEducationModalOpen(false)}
              application={application}
            />
          </Modal>

          <Modal
            open={priorLearningModalOpen}
            onOpenChange={setPriorLearningModalOpen}
          >
            <PriorLearningModal
              referenceId={application.user.sandId!}
              close={() => setPriorLearningModalOpen(false)}
              application={application}
            />
          </Modal>

          <Modal
            open={enrollmentAgreementModalOpen}
            onOpenChange={setEnrollmentAgreementModalOpen}
          >
            <EnrollmentAgreementModal
              docusignLink={application?.docusignEnrollmentAgreement?.link}
              close={() => setEnrollmentAgreementModalOpen(false)}
            />
          </Modal>
        </>
      )}
    </>
  );
};
